import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, Sun, Moon, User, Compass, Eye, FileText, Check, Clock, Shield, Target, Users, Lightbulb, BookOpen, Gem, TrendingUp, AlertTriangle, Zap, Layers, Calendar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface ComboAnalysisPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-21522">
      {children}
    </motion.section>;
}
export function ComboAnalysisPage({
  onNavigate
}: ComboAnalysisPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPremium = () => {
    addItem({
      id: `premium-combo-${Date.now()}`,
      name: 'Premium комбо: Натал + Соляр',
      description: 'Полный профессиональный отчёт с глубоким анализом натальной карты и соляра, практическими рекомендациями.',
      price: 6900,
      type: 'premium-report',
      serviceName: 'Комбо: Натал + Соляр',
      format: 'PDF-отчёт',
      duration: '50-60 страниц',
      badges: [{
        label: 'Premium',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-21523">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-21524">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-violet-600/15 rounded-full blur-3xl pointer-events-none" data-id="element-21525" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-21526" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-21527">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Комбо: Натал + Соляр'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-21528" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-21529">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-21530">
              <motion.div variants={staggerItem} className="mb-4 sm:mb-5" data-id="element-21531">
                <Badge variant="pro" className="bg-violet-500/20 text-violet-300 border-violet-500/30 px-3 py-1 text-sm" data-id="element-21532">
                  Выгода 17%
                </Badge>
              </motion.div>

              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-21533">
                Полный астрологический анализ вашей жизни и прогноза на год
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-21534">
                Комбинированный отчёт объединяет натальную карту и соляр, чтобы
                показать вашу личность, жизненные задачи и основные события
                ближайшего года.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-21535">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-violet-500/25 bg-violet-600 hover:bg-violet-700" data-id="element-21536">
                  Получить полный анализ
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-21537" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-21538">
                {[{
                icon: <User className="w-4 h-4" data-id="element-21539" />,
                text: 'Анализ вашей личности'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-21540" />,
                text: 'Раскрытие талантов и предназначения'
              }, {
                icon: <Calendar className="w-4 h-4" data-id="element-21541" />,
                text: 'Прогноз событий на год'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-21542" />,
                text: 'Рекомендации для решений'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-21543">
                    <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-300 flex-shrink-0" data-id="element-21544">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — two overlapping circles */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-21545">
              <div className="relative w-[420px] h-[420px]" data-id="element-21546">
                {/* Natal circle */}
                <div className="absolute left-0 top-[10%] w-[280px] h-[280px] rounded-full border border-indigo-500/25 flex items-center justify-center" data-id="element-21547">
                  <div className="w-40 h-40 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-3xl opacity-20" data-id="element-21548" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-21549">
                    <Moon className="w-14 h-14 text-indigo-300/60" strokeWidth={1} data-id="element-21550" />
                  </div>
                  <div className="absolute inset-0 rounded-full border border-indigo-400/10 animate-[spin_50s_linear_infinite]" data-id="element-21551" />
                </div>
                {/* Solar circle */}
                <div className="absolute right-0 bottom-[10%] w-[280px] h-[280px] rounded-full border border-amber-500/25 flex items-center justify-center" data-id="element-21552">
                  <div className="w-40 h-40 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-3xl opacity-20" data-id="element-21553" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-21554">
                    <Sun className="w-14 h-14 text-amber-300/60" strokeWidth={1} data-id="element-21555" />
                  </div>
                  <div className="absolute inset-0 rounded-full border border-amber-400/10 animate-[spin_40s_linear_infinite_reverse]" data-id="element-21556" />
                </div>
                {/* Intersection glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-violet-400 to-fuchsia-500 rounded-full blur-2xl opacity-30 animate-pulse" data-id="element-21557" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-21558">
                  <Layers className="w-10 h-10 text-white/70" strokeWidth={1.5} data-id="element-21559" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. ЧТО ВХОДИТ В КОМБО-АНАЛИЗ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21560">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21561">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 text-center" data-id="element-21562">
            Что входит в комбо-анализ
          </h2>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21563">
            Отчёт состоит из двух взаимодополняющих частей
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto" data-id="element-21564">
            {/* Natal */}
            <Card padding="lg" className="border-indigo-100 relative overflow-hidden" data-id="element-21565">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-21566" />
              <div className="relative z-10" data-id="element-21567">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-6" data-id="element-21568">
                  <Moon className="w-7 h-7" data-id="element-21569" />
                </div>
                <Badge variant="neutral" className="mb-4 bg-indigo-50 text-indigo-700 border-indigo-100" data-id="element-21570">
                  Часть 1
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-21571">
                  Натальная карта
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-21572">
                  Натальная карта показывает особенности вашей личности, сильные
                  стороны, таланты, жизненные задачи и потенциал развития. Это
                  фундамент, на котором строится понимание вашей природы.
                </p>
              </div>
            </Card>

            {/* Solar */}
            <Card padding="lg" className="border-amber-100 relative overflow-hidden" data-id="element-21573">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-21574" />
              <div className="relative z-10" data-id="element-21575">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-6" data-id="element-21576">
                  <Sun className="w-7 h-7" data-id="element-21577" />
                </div>
                <Badge variant="neutral" className="mb-4 bg-amber-50 text-amber-700 border-amber-100" data-id="element-21578">
                  Часть 2
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-21579">
                  Соляр (прогноз на год)
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-21580">
                  Соляр — это прогноз на год, который показывает главные
                  события, возможности и вызовы в период от одного дня рождения
                  до следующего. Это ваша дорожная карта на ближайший год.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. ПОЧЕМУ ЛУЧШЕ ВМЕСТЕ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21582">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21583">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21584">
              Почему лучше делать анализ вместе
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21585">
              Два анализа дают более точную и полную картину вашей жизни
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 max-w-6xl mx-auto" data-id="element-21586">
            {/* Natal column */}
            <Card padding="lg" className="border-indigo-100" data-id="element-21587">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-5" data-id="element-21588">
                <Moon className="w-6 h-6" data-id="element-21589" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-21590">
                Натальная карта показывает
              </h3>
              <ul className="space-y-3" data-id="element-21591">
                {['Кто вы по своей природе', 'Ваши сильные стороны', 'Ваши внутренние мотивации', 'Таланты и предназначение'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-21592">
                    <Check className="w-4 h-4 text-indigo-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-21593" />
                    {t}
                  </li>)}
              </ul>
            </Card>

            {/* Combined — center */}
            <Card padding="lg" className="border-violet-200 ring-1 ring-violet-100 shadow-lg shadow-violet-100/50" data-id="element-21594">
              <div className="w-12 h-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-5" data-id="element-21595">
                <Layers className="w-6 h-6" data-id="element-21596" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-21597">
                Вместе они дают
              </h3>
              <ul className="space-y-3" data-id="element-21598">
                {['Полное понимание себя и своего года', 'Связь между характером и событиями', 'Точные рекомендации с учётом личности', 'Максимально полный анализ'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-700 font-medium" data-id="element-21599">
                    <Sparkles className="w-4 h-4 text-violet-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-21600" />
                    {t}
                  </li>)}
              </ul>
            </Card>

            {/* Solar column */}
            <Card padding="lg" className="border-amber-100" data-id="element-21601">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5" data-id="element-21602">
                <Sun className="w-6 h-6" data-id="element-21603" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-21604">
                Соляр показывает
              </h3>
              <ul className="space-y-3" data-id="element-21605">
                {['Какие события ждут в этом году', 'Где будут возможности', 'Какие темы будут важнейшими', 'Периоды роста и осторожности'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-21606">
                    <Check className="w-4 h-4 text-amber-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-21607" />
                    {t}
                  </li>)}
              </ul>
            </Card>
          </div>
        </div>
      </AnimatedSection>

      {/* 4. ЧТО ВЫ УЗНАЕТЕ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21608">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21609">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21610">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21611">
              Что вы узнаете из отчёта
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21612">
              Шесть ключевых направлений анализа вашей жизни
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-21613">
            {[{
            icon: <User className="w-6 h-6" data-id="element-21614" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личность и характер',
            desc: 'Глубокий анализ психологических особенностей, темперамента и способа восприятия мира.'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-21615" />,
            color: 'bg-violet-50 text-violet-600',
            title: 'Таланты и предназначение',
            desc: 'Какие способности заложены в вашей карте и как их раскрыть в полной мере.'
          }, {
            icon: <Heart className="w-6 h-6" data-id="element-21616" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Отношения и любовь',
            desc: 'Как вы строите отношения, какие партнёры вам подходят и что мешает гармонии.'
          }, {
            icon: <Briefcase className="w-6 h-6" data-id="element-21617" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и деньги',
            desc: 'Какие направления деятельности наиболее благоприятны и где искать финансовый успех.'
          }, {
            icon: <Star className="w-6 h-6" data-id="element-21618" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Главные темы года',
            desc: 'Какие события могут быть наиболее важными в течение ближайшего года жизни.'
          }, {
            icon: <AlertTriangle className="w-6 h-6" data-id="element-21619" />,
            color: 'bg-orange-50 text-orange-600',
            title: 'Возможности и вызовы',
            desc: 'Периоды роста и возможные трудности — когда действовать, а когда выжидать.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} data-id="element-21620">
                <Card padding="lg" className="h-full border-slate-100" data-id="element-21621">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mb-4 sm:mb-5`} data-id="element-21622">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-21623">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21624">
                    {item.desc}
                  </p>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* 5. СТРУКТУРА ПОЛНОГО ОТЧЁТА */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21625">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21626">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21627">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21628">
              Структура полного отчёта
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21629">
              Более 50 страниц персонального анализа в двух частях
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto" data-id="element-21630">
            {/* Part 1 — Natal */}
            <div data-id="element-21631">
              <div className="flex items-center gap-3 mb-6" data-id="element-21632">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center" data-id="element-21633">
                  <Moon className="w-5 h-5" data-id="element-21634" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" data-id="element-21635">
                  Часть 1 — Натальная карта
                </h3>
              </div>
              <div className="space-y-3" data-id="element-21636">
                {[{
                icon: <Sun className="w-4 h-4" data-id="element-21637" />,
                text: 'Анализ Солнца, Луны и Асцендента'
              }, {
                icon: <Star className="w-4 h-4" data-id="element-21638" />,
                text: 'Планеты в знаках зодиака'
              }, {
                icon: <BookOpen className="w-4 h-4" data-id="element-21639" />,
                text: 'Планеты в домах гороскопа'
              }, {
                icon: <Gem className="w-4 h-4" data-id="element-21640" />,
                text: 'Аспекты между планетами'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-21641" />,
                text: 'Таланты и предназначение'
              }, {
                icon: <User className="w-4 h-4" data-id="element-21642" />,
                text: 'Психологический профиль'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-slate-100" data-id="element-21643">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-21644">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-21645">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Part 2 — Solar */}
            <div data-id="element-21646">
              <div className="flex items-center gap-3 mb-6" data-id="element-21647">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-21648">
                  <Sun className="w-5 h-5" data-id="element-21649" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" data-id="element-21650">
                  Часть 2 — Соляр
                </h3>
              </div>
              <div className="space-y-3" data-id="element-21651">
                {[{
                icon: <Star className="w-4 h-4" data-id="element-21652" />,
                text: 'Общая тема года'
              }, {
                icon: <BookOpen className="w-4 h-4" data-id="element-21653" />,
                text: 'Активные дома соляра'
              }, {
                icon: <Zap className="w-4 h-4" data-id="element-21654" />,
                text: 'Влияние планет в солярной карте'
              }, {
                icon: <Briefcase className="w-4 h-4" data-id="element-21655" />,
                text: 'Прогноз по основным сферам жизни'
              }, {
                icon: <AlertTriangle className="w-4 h-4" data-id="element-21656" />,
                text: 'Зоны риска и предупреждения'
              }, {
                icon: <Compass className="w-4 h-4" data-id="element-21657" />,
                text: 'Рекомендации на год'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-slate-100" data-id="element-21658">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-21659">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-21660">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ПРИМЕР ОТЧЁТА */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-21661">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21662">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-21663">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-21664">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-21665">
                <span className="text-xs font-bold text-violet-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-21666">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-21667">
                  Два профессиональных отчёта в одном документе
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-21668">
                  Комбо-отчёт содержит графические схемы обеих карт, подробный
                  анализ личности и годовой прогноз с практическими
                  рекомендациями.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-21669">
                  {['Графическая схема натальной карты', 'Графическая схема солярной карты', 'Персональный анализ личности', 'Прогноз по сферам жизни на год', 'Более 50 страниц в PDF'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-21670">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-21671" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-combo')} data-id="element-21672">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-21673">
                <div className="relative" data-id="element-21674">
                  {/* Natal report page */}
                  <motion.div animate={{
                  y: [0, -6, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="w-56 bg-white rounded-xl shadow-2xl p-6 relative z-10" data-id="element-21675">
                    <div className="w-full h-20 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg mb-4 flex items-center justify-center" data-id="element-21676">
                      <div className="w-12 h-12 rounded-full border-2 border-indigo-200 flex items-center justify-center" data-id="element-21677">
                        <Moon className="w-5 h-5 text-indigo-300" data-id="element-21678" />
                      </div>
                    </div>
                    <div className="w-3/4 h-2.5 bg-slate-200 rounded mb-2" data-id="element-21679" />
                    <div className="w-1/2 h-2.5 bg-slate-100 rounded mb-4" data-id="element-21680" />
                    <div className="space-y-2" data-id="element-21681">
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-21682" />
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-21683" />
                      <div className="w-4/5 h-1.5 bg-slate-50 rounded" data-id="element-21684" />
                    </div>
                  </motion.div>
                  {/* Solar report page — offset */}
                  <motion.div animate={{
                  y: [0, -4, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5
                }} className="w-56 bg-white rounded-xl shadow-xl p-6 absolute top-12 left-16 z-20" data-id="element-21685">
                    <div className="w-full h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg mb-4 flex items-center justify-center" data-id="element-21686">
                      <div className="w-12 h-12 rounded-full border-2 border-amber-200 flex items-center justify-center" data-id="element-21687">
                        <Sun className="w-5 h-5 text-amber-400" data-id="element-21688" />
                      </div>
                    </div>
                    <div className="w-3/4 h-2.5 bg-slate-200 rounded mb-2" data-id="element-21689" />
                    <div className="w-1/2 h-2.5 bg-slate-100 rounded mb-4" data-id="element-21690" />
                    <div className="flex items-end gap-1 h-8 mb-3" data-id="element-21691">
                      {[50, 70, 40, 85, 60, 45, 75, 55].map((h, i) => <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-amber-200 to-amber-100" style={{
                      height: `${h}%`
                    }} data-id="element-21692" />)}
                    </div>
                    <div className="space-y-2" data-id="element-21693">
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-21694" />
                      <div className="w-3/4 h-1.5 bg-slate-50 rounded" data-id="element-21695" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. ДЛЯ КОГО */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21696">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21697">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-21698">
            Для кого подходит этот анализ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-21699">
            {[{
            icon: <Eye className="w-5 h-5" data-id="element-21700" />,
            text: 'Людям, которые хотят лучше понять себя и свою природу'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-21701" />,
            text: 'Тем, кто планирует важные решения в ближайший год'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-21702" />,
            text: 'Людям, интересующимся астрологией и самопознанием'
          }, {
            icon: <Calendar className="w-5 h-5" data-id="element-21703" />,
            text: 'Тем, кто хочет знать тенденции ближайшего года'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-21704" />,
            text: 'Тем, кто ищет своё предназначение и подходящий путь'
          }, {
            icon: <Users className="w-5 h-5" data-id="element-21705" />,
            text: 'Тем, кто хочет подарить близким полезный и уникальный подарок'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-21706">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0" data-id="element-21707">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-21708">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 8. ПОЧЕМУ САМЫЙ ПОЛНЫЙ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21709">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21710">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21711">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21712">
              Почему этот анализ самый полный
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto" data-id="element-21713">
            {[{
            icon: <User className="w-6 h-6" data-id="element-21714" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Анализ личности',
            desc: 'Полный разбор характера, талантов, мотиваций и психологических особенностей по натальной карте.'
          }, {
            icon: <Compass className="w-6 h-6" data-id="element-21715" />,
            color: 'bg-violet-100 text-violet-600',
            title: 'Жизненный потенциал',
            desc: 'Понимание кармических задач, предназначения и ключевых направлений развития.'
          }, {
            icon: <TrendingUp className="w-6 h-6" data-id="element-21716" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Прогноз на год',
            desc: 'Детальный прогноз событий, возможностей и вызовов на ближайший год по солярной карте.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100 text-center" data-id="element-21717">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4 sm:mb-5`} data-id="element-21718">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-21719">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21720">
                  {item.desc}
                </p>
              </Card>)}
          </div>
          <p className="text-center text-slate-600 mt-10 max-w-2xl mx-auto" data-id="element-21721">
            Это позволяет получить максимально полное представление о своей
            жизни, возможностях и направлении движения.
          </p>
        </div>
      </AnimatedSection>

      {/* 9. CTA */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-21722">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-21723">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-21724">
            <motion.div className="flex items-center justify-center gap-3 mb-4 sm:mb-6" animate={{
            scale: [1, 1.08, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-21725">
              <Moon className="w-7 h-7 text-indigo-400" data-id="element-21726" />
              <Layers className="w-6 h-6 text-violet-400" data-id="element-21727" />
              <Sun className="w-7 h-7 text-amber-400" data-id="element-21728" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-21729">
              Получите полный астрологический анализ
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-21730">
              Введите дату, время и место рождения, чтобы получить персональный
              отчёт с анализом личности и прогнозом на год.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-21731">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-violet-500/25 bg-violet-600 hover:bg-violet-700" data-id="element-21732">
                Построить натальную карту и соляр
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-21733" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-21734">
              <span className="flex items-center" data-id="element-21735">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-21736" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-21737">
                <Clock className="w-4 h-4 mr-1.5 text-violet-400" data-id="element-21738" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* 10. ТАРИФЫ */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-21739">
        <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-21740">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21741">
            <div className="text-center mb-8 sm:mb-16" data-id="element-21742">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21743">
                Выберите формат комбо-отчёта
              </h2>
              <p className="text-base sm:text-lg text-slate-600" data-id="element-21744">
                Два варианта — от краткого обзора до полного профессионального
                анализа
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8" data-id="element-21745">
              {/* Beta */}
              <Card padding="lg" className="flex flex-col border-slate-200" data-id="element-21746">
                <Badge variant="neutral" className="w-fit mb-6" data-id="element-21747">
                  Базовый
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-21748">
                  Beta комбо
                </h3>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-21749">
                  Краткий анализ натальной карты и годового прогноза — основные
                  показатели личности и ключевые тенденции года.
                </p>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" data-id="element-21750">
                  {['Анализ Солнца, Луны и Асцендента', 'Основные черты характера', 'Общая тема года (соляр)', 'Краткий прогноз по сферам', 'PDF-отчёт на 1-3 страницы', 'Готовность — мгновенно'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-21751">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-21752" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-emerald-600 mb-6" data-id="element-21753">
                  Бесплатно
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate('free-chart')} data-id="element-21754">
                  Получить Beta комбо
                </Button>
              </Card>

              {/* Premium */}
              <Card padding="lg" className="flex flex-col border-violet-200 ring-1 ring-violet-100 shadow-xl shadow-violet-100/50 relative" data-id="element-21755">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 bg-violet-600 text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-21756">
                  Рекомендуем
                </div>
                <Badge variant="pro" className="w-fit mb-6" data-id="element-21757">
                  Полный
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-21758">
                  Premium комбо
                </h3>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-21759">
                  Полный профессиональный отчёт с глубоким анализом натальной
                  карты и соляра, практическими рекомендациями.
                </p>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" data-id="element-21760">
                  {['Всё из Beta комбо', 'Все планеты в знаках и домах', 'Аспекты натальной карты', 'Полный анализ солярной карты', 'Помесячный прогноз на год', 'Кармические задачи и предназначение', 'Компенсаторика и рекомендации', 'PDF-отчёт на 50-60 страниц'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-21761">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-21762" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-21763">
                  6 900 ₽
                </div>
                <Button variant="primary" fullWidth onClick={handleBuyPremium} className="bg-violet-600 hover:bg-violet-700" data-id="element-21764">
                  Получить Premium комбо
                </Button>
              </Card>
            </div>

            <p className="text-center text-sm text-slate-500 mt-8 flex items-center justify-center gap-4" data-id="element-21765">
              <span className="flex items-center" data-id="element-21766">
                <Shield className="w-4 h-4 mr-1 text-emerald-500" data-id="element-21767" /> Безопасная
                оплата
              </span>
              <span className="flex items-center" data-id="element-21768">
                <Clock className="w-4 h-4 mr-1 text-violet-500" data-id="element-21769" /> Мгновенная
                доставка
              </span>
            </p>
          </div>
        </AnimatedSection>
      </div>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21770">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21771">
          <div className="text-center mb-8 sm:mb-12" data-id="element-21772">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21773">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Чем комбо-отчёт отличается от покупки двух отчётов по отдельности?',
          content: 'Комбо-отчёт стоит дешевле, чем два отдельных отчёта. Кроме того, в Premium версии анализ интегрирован — соляр интерпретируется с учётом вашей натальной карты, что даёт более точные и персонализированные рекомендации.'
        }, {
          id: '2',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Да, для точного расчёта обеих карт необходимо знать время рождения. Оно влияет на положение Асцендента, домов гороскопа и солярной карты. Если время неизвестно, наши астрологи могут провести ректификацию.'
        }, {
          id: '3',
          title: 'Как быстро я получу отчёт?',
          content: 'Мгновенно. Сразу после оплаты система сгенерирует PDF-файл с обеими частями отчёта. Вы сможете скачать его на странице успеха, также он будет отправлен на email.'
        }, {
          id: '4',
          title: 'Когда лучше заказывать комбо-отчёт?',
          content: 'Идеальное время — за 1-2 месяца до дня рождения или сразу после. Так вы получите и анализ личности, и актуальный прогноз на предстоящий год.'
        }, {
          id: '5',
          title: 'Чем Beta комбо отличается от Premium?',
          content: 'Beta содержит краткий анализ ключевых показателей и общий прогноз (1-3 страницы). Premium — это полный профессиональный разбор всех планет, домов, аспектов обеих карт, помесячный прогноз и практические рекомендации (50-60 страниц).'
        }]} data-id="element-21774" />
        </div>
      </AnimatedSection>
    </div>;
}